import React, { SVGProps } from 'react';
import classNames from 'classnames';

export interface IStackedIconProps {
  // For overlay mode (background + foreground centered)
  BackgroundIcon?: React.FC<SVGProps<SVGSVGElement>>;
  ForegroundIcon?: React.FC<SVGProps<SVGSVGElement>>;
  backgroundClassName?: string;
  foregroundClassName?: string;

  // For vertical stacking mode (icons stacked vertically)
  TopIcon?: React.FC<SVGProps<SVGSVGElement>>;
  BottomIcon?: React.FC<SVGProps<SVGSVGElement>>;
  topClassName?: string;
  bottomClassName?: string;

  // Common props
  stackDirection?: 'overlay' | 'vertical';
  className?: string;
  wrapperClassName?: string;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

/**
 * StackedIcon component for layering multiple SVG icons
 *
 * Replaces Font Awesome's fa-stack pattern with native SVG components.
 * Icons are rendered directly without SvgIcon wrapper for precise control.
 *
 * Supports two modes:
 * - 'overlay': Centers foreground icon over background icon (e.g., plus on square)
 * - 'vertical': Stacks icons vertically (e.g., sort icons with up/down arrows)
 *
 * @example Overlay mode (for transaction icons)
 * <StackedIcon
 *   BackgroundIcon={SquareIcon}
 *   ForegroundIcon={PlusIcon}
 *   backgroundClassName="w-8 h-8 fill-green-40"
 *   foregroundClassName="w-4 h-4 fill-black"
 *   stackDirection="overlay"
 * />
 *
 * @example Vertical mode (for sort icons)
 * <StackedIcon
 *   TopIcon={ChevronUpIcon}
 *   BottomIcon={ChevronDownIcon}
 *   topClassName="w-4 h-4 fill-transparent stroke-gray-100 stroke-2"
 *   bottomClassName="w-4 h-4 fill-transparent stroke-gray-40 stroke-2"
 *   stackDirection="vertical"
 *   className="-space-y-2"
 * />
 */
export const StackedIcon: React.FC<IStackedIconProps> = ({
  BackgroundIcon,
  ForegroundIcon,
  backgroundClassName,
  foregroundClassName,
  TopIcon,
  BottomIcon,
  topClassName,
  bottomClassName,
  stackDirection = 'overlay',
  className,
  wrapperClassName,
  onClick,
}) => {
  if (stackDirection === 'overlay') {
    // Overlay mode: center foreground icon over background icon using flexbox
    return (
      <span
        className={classNames('relative inline-flex items-center justify-center', wrapperClassName)}
        onClick={onClick}
      >
        {BackgroundIcon && <BackgroundIcon className={backgroundClassName} />}
        {ForegroundIcon && <ForegroundIcon className={classNames('absolute', foregroundClassName)} />}
      </span>
    );
  } else {
    // Vertical mode: stack icons vertically (used for sort indicators)
    return (
      <span
        className={classNames('inline-flex flex-col items-center -space-y-2', className, wrapperClassName)}
        onClick={onClick}
      >
        {TopIcon && <TopIcon className={topClassName} />}
        {BottomIcon && <BottomIcon className={bottomClassName} />}
      </span>
    );
  }
};
